package uk.co.nickthecoder.shaders

import org.khronos.webgl.WebGLProgram
import org.khronos.webgl.WebGLRenderingContext.Companion.ARRAY_BUFFER
import org.khronos.webgl.WebGLRenderingContext.Companion.FLOAT
import org.khronos.webgl.WebGLRenderingContext.Companion.FRAGMENT_SHADER
import org.khronos.webgl.WebGLRenderingContext.Companion.LINK_STATUS
import org.khronos.webgl.WebGLRenderingContext.Companion.TRIANGLES
import org.khronos.webgl.WebGLRenderingContext.Companion.TRIANGLE_STRIP
import org.khronos.webgl.WebGLRenderingContext.Companion.VERTEX_SHADER
import org.khronos.webgl.WebGLUniformLocation
import uk.co.nickthecoder.glok.backend.backend
import uk.co.nickthecoder.glok.backend.webgl.WebGLBackend
import uk.co.nickthecoder.glok.scene.Color
import uk.co.nickthecoder.glok.util.Matrix
import uk.co.nickthecoder.glok.util.log
import uk.co.nickthecoder.kyd.shapes.Mesh
import uk.co.nickthecoder.kyd.shapes.MeshBuffer

/**
 * Unlike Glok's program, these programs use vertex buffers that are already built
 * (within the [Mesh]'s [MeshBuffer])
 */
actual abstract class Program(vertexCode: String, fragmentCode: String, private val floatsPerVertex: Int) {

    actual open val hasAcrossAttribute get() = false

    protected val program = createProgram(vertexCode, fragmentCode)

    private val viewLocation = getUniformLocation("viewMatrix")
    private val modelLocation = getUniformLocation("modelMatrix")
    protected val positionLocation = getAttributeLocation("position")

    protected fun getUniformLocation(name: String): WebGLUniformLocation? {
        val result = WebGLBackend.gl.getUniformLocation(program, name)
        if (result == null) {
            log.error("Failed to get Uniform $name")
        }
        return result
    }

    protected fun getAttributeLocation(name: String): Int {
        val result = WebGLBackend.gl.getAttribLocation(program, name)
        if (result < 0) {
            log.error("Failed to get Attribute $name")
        }
        return result
    }

    protected fun setUniform(location: WebGLUniformLocation?, value: Color?) {
        val color = value ?: Color.WHITE
        WebGLBackend.gl.uniform4f(location, color.red, color.green, color.blue, color.alpha)
    }

    protected fun setUniform(location: WebGLUniformLocation?, value: Float) {
        WebGLBackend.gl.uniform1f(location, value)
    }

    protected fun setUniform(location: WebGLUniformLocation?, value: Matrix?) {
        val matrix = value ?: Matrix.IDENTITY
        WebGLBackend.gl.uniformMatrix3fv(location, false, matrix.asFlat3x3Array)
    }

    protected fun begin(viewMatrix: Matrix, modelMatrix : Matrix) {
        WebGLBackend.gl.useProgram(program)
        setUniform(viewLocation, viewMatrix)
        setUniform(modelLocation, modelMatrix)
    }

    protected abstract fun enableAttributes()
    protected abstract fun disableAttributes()

    fun draw(mesh: Mesh) {
        with(WebGLBackend.gl) {
            mesh.meshBuffer.bind()
            enableAttributes()

            if (mesh.floatsPerVertex != floatsPerVertex) {
                log.error("Mismatched floatsPerVertex")
                return
            }
            if (mesh.meshBuffer.floatCount.rem(floatsPerVertex) != 0) {
                log.error("Incorrect floatCount. Expected a multiple of $floatsPerVertex, found ${mesh.meshBuffer.floatCount}")
                return
            }
            drawArrays(
                if (mesh.isTriangleStrip) TRIANGLE_STRIP else TRIANGLES,
                0,
                mesh.meshBuffer.floatCount / mesh.floatsPerVertex
            )
        }
    }

    protected open fun end() {
        with(WebGLBackend.gl) {
            disableAttributes()
            bindBuffer(ARRAY_BUFFER, null)
            useProgram(null)
        }
    }

    companion object {

        private fun createProgram(vertexShaderCode: String, fragmentShaderCode: String): WebGLProgram? {
            with(WebGLBackend.gl) {
                val program = createProgram()

                val vertexShader = createShader(VERTEX_SHADER).also { shader ->
                    shaderSource(shader, vertexShaderCode)
                    compileShader(shader)
                    //if (getShaderi(shader, GL_COMPILE_STATUS) != GL_TRUE) {
                    //    log.error("Vertex shader compilation failed : '${glGetShaderInfoLog(shader)}'")
                    //}
                }
                val fragmentShader = createShader(FRAGMENT_SHADER).also { shader ->
                    shaderSource(shader, fragmentShaderCode)
                    compileShader(shader)
                    //if (glGetShaderi(shader, GL_COMPILE_STATUS) != GL_TRUE) {
                    //    log.error("Fragment shader compilation failed : '${glGetShaderInfoLog(shader)}'")
                    //}
                }
                attachShader(program, vertexShader)
                attachShader(program, fragmentShader)
                linkProgram(program)
                if (getProgramParameter(program, LINK_STATUS) != true) {
                    log.error("Unable to initialize the shader program: ${getProgramInfoLog(program)}")
                    return null
                }
                deleteShader(vertexShader)
                deleteShader(fragmentShader)

                if (backend.reportError()) {
                    log.error("Failed to create shader program")
                }

                return program
            }
        }
    }
}

abstract class SimpleProgram(vertexCode: String, fragmentCode: String) : Program(vertexCode, fragmentCode, 2) {

    override fun enableAttributes() {
        with(WebGLBackend.gl) {
            vertexAttribPointer(positionLocation, 2, FLOAT, false, 8, 0)
            enableVertexAttribArray(positionLocation)
        }
    }

    override fun disableAttributes() {
        with(WebGLBackend.gl) {
            disableVertexAttribArray(positionLocation)
        }
    }

}

abstract class UProgram(vertexCode: String, fragmentCode: String) : Program(vertexCode, fragmentCode, 3) {

    private val uLocation = getAttributeLocation("u")

    override fun enableAttributes() {
        with(WebGLBackend.gl) {
            vertexAttribPointer(positionLocation, 2, FLOAT, false, 12, 0)
            vertexAttribPointer(uLocation, 1, FLOAT, false, 12, 8)
            enableVertexAttribArray(positionLocation)
            enableVertexAttribArray(uLocation)
        }
    }

    override fun disableAttributes() {
        with(WebGLBackend.gl) {
            disableVertexAttribArray(positionLocation)
            disableVertexAttribArray(uLocation)
        }
    }

}

abstract class UVProgram(vertexCode: String, fragmentCode: String) : Program(vertexCode, fragmentCode, 4) {

    private val uvLocation = getAttributeLocation("uv")

    override fun enableAttributes() {
        with(WebGLBackend.gl) {
            vertexAttribPointer(positionLocation, 2, FLOAT, false, 16, 0)
            vertexAttribPointer(uvLocation, 2, FLOAT, false, 16, 8)

            enableVertexAttribArray(positionLocation)
            enableVertexAttribArray(uvLocation)
        }
    }

    override fun disableAttributes() {
        with(WebGLBackend.gl) {
            enableVertexAttribArray(positionLocation)
            enableVertexAttribArray(uvLocation)
        }
    }

}
